/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

interface AppError {
    msg: string;
    msgCode: number;
}

const messages: Record<string, AppError> = {
    /** Codes - 500,501,502  **/
    1000: {msg: 'Nu s-a creat niciun document.', msgCode: 1000},
    1001: {msg: 'A fost o problemă la crearea utilizatorului!', msgCode: 1001},
    1002: {msg: 'Adresa de email nu este corectă. Te rugăm sa încerci din nou!', msgCode: 1002},
    1003: {msg: 'Ceva nu a mers bine! Te rugăm sa încerci din nou mai târziu.', msgCode: 1003},
    1004: {msg: 'URL-ul există deja. Te rugăm să utilizezi altul și să încerci din nou.', msgCode: 1004},
    1005: {msg: 'Numele există deja. Te rugăm să utilizezi un alt nume și să încerci din nou.', msgCode: 1005},
    1006: {msg: 'Emailul există deja. Te rugăm să utilizezi un alt email și să încerci din nou.', msgCode: 1006},
    1068: {msg: 'Fișierul încărcat depășește dimensiunea maximă de încărcare.', msgCode: 1068},
    1069: {msg: 'Numărul de fișiere încărcate depășește numărul maxim permis.', msgCode: 1069},
    1070: {msg: 'Numele fișierului încărcat este prea lung.', msgCode: 1070},
    1071: {msg: 'Încărcare neașteptată. Te rugăm sa încerci din nou.', msgCode: 1071},
    1077: {msg: 'Eroare de la procesatorul de plată. Te rugăm să încerci din nou.', msgCode: 1077},
    1078: {msg: 'Nu s-a putut valida plata. Te rugăm să încerci din nou.', msgCode: 1078},
    1079: {msg: 'Nu s-a putut inițializa setup-ul. Te rugăm să încerci din nou.', msgCode: 1079},
    1080: {msg: 'Setup-ul pentru acest website a fost deja realizat!', msgCode: 1080},
    1081: {msg: 'Acest email este deja înregistrat. Te rugăm sa folosești o altă adresă.', msgCode: 1081},
    1082: {msg: 'Acest URL este deja folosit de un alt website. Te rugăm sa folosești o altă adresă.', msgCode: 1082},
    1083: {msg: 'Procesul de plată nu poate fi inițializat.', msgCode: 1083},
    1084: {msg: 'A intervenit o eroare în procesul de instalare. Te rugăm să încerci din nou.', msgCode: 1084},
    1103: {msg: 'Codul introdus nu există. Te rugăm să încerci din nou.', msgCode: 1103},



    /** Codes - 400,401,402,403,404 **/
    1007: {msg: 'Nu s-a găsit niciun document.', msgCode: 1007},
    1008: {msg: 'Nu s-au găsit documente.', msgCode: 1008},
    1009: {msg: 'Nu aveți permisiunea de a efectua această acțiune!', msgCode: 1009},
    1010: {msg: 'Te rugăm să furnizezi un emailul și o parolă!', msgCode: 1010},
    1011: {msg: 'Nu s-a găsit niciun utilizator cu această adresă de email.', msgCode: 1011},
    1012: {msg: 'Valoarea câmpului duplicat. Te rugăm să utilizezi o altă valoare.', msgCode: 1012},
    1013: {msg: 'Eroare de transformare pentru următoarele elemente: .', msgCode: 1013},
    1014: {msg: 'Email sau parolă incorectă.', msgCode: 1014},
    1015: {msg: 'Emailul introdus nu există.', msgCode: 1015},
    1016: {
        msg: 'Te rugăm să te autentifici în contul tău de utilizator pentru a accesa această pagină.',
        msgCode: 1016
    },
    1017: {msg: 'Parola a fost schimbată recent. Te rugăm să te autentifici din nou.', msgCode: 1017},
    1018: {msg: 'Token-ul a expirat sau este invalid', msgCode: 1018},
    1019: {msg: 'Token-ul este invalid', msgCode: 1019},
    1020: {msg: 'Parolele nu sunt identice. Te rugăm sa încerci din nou.', msgCode: 1020},
    1021: {msg: 'Actualizați parola prin link-ul de uitat parola.', msgCode: 1021},
    1022: {msg: 'Pagina nu a fost găsită!', msgCode: 1022},
    1067: {msg: 'Fișierul/Directorul nu există.', msgCode: 1067},
    1108: {msg: 'Codul introdus nu există.', msgCode: 1108},
    1161: {msg: 'Cod incompatibil cu pachetul ales.', msgCode: 1161},

    /** Codes - 200, 201, no code **/
    1023: {msg: 'Documentul a fost șters cu succes.', msgCode: 1023},
    1024: {msg: 'Un utilizator trebuie să conțină un prenume și un nume de familie!', msgCode: 1024},
    1025: {msg: 'Un utilizator trebuie să conțină o parolă!', msgCode: 1025},
    1026: {msg: 'Lipsește ID-ul nunții.', msgCode: 1026},
    1027: {msg: 'Te rugăm să confirmi parola.', msgCode: 1027},
    1028: {msg: 'URL-ul nu este valid. Te rugăm sa încerci din nou.', msgCode: 1028},
    1029: {msg: 'URL-ul nu poate fi gol. Te rugăm sa încerci din nou.', msgCode: 1029},
    1030: {msg: 'Te rugăm să furnizezi o adresă de email validă.', msgCode: 1030},
    1031: {msg: 'Lipsește URL-ul site-ului web!', msgCode: 1031},
    1032: {msg: 'Un utilizator trebuie să conțină un email!', msgCode: 1032},
    1033: {msg: 'Elementul trebuie să conțină un nume!', msgCode: 1033},
    1034: {msg: 'Adresa este obligatorie', msgCode: 1034},
    1035: {msg: 'Județul este obligatoriu', msgCode: 1035},
    1036: {msg: 'Orașul este obligatoriu', msgCode: 1036},
    1037: {msg: 'Codul poștal este obligatoriu', msgCode: 1037},
    1038: {msg: 'Numărul străzii este obligatoriu', msgCode: 1038},
    1039: {msg: 'O colecție trebuie să conțină un nume!', msgCode: 1039},
    1040: {msg: 'Un element trebuie să conțină un preț.', msgCode: 1040},
    1041: {msg: 'Elementul trebuie să conțină un număr de imagini.', msgCode: 1041},
    1042: {msg: 'Elementul trebuie să conțină o durată de uptime.', msgCode: 1042},
    1043: {msg: 'Elementul trebuie să conțină o opțiune de mail RSVP.', msgCode: 1043},
    1044: {msg: 'Elementul trebuie să conțină o opțiune de galerie sus.', msgCode: 1044},
    1045: {msg: 'Elementul trebuie să conțină o opțiune de poveste a cuplului.', msgCode: 1045},
    1046: {msg: 'Elementul trebuie să conțină o opțiune de eveniment nuntă.', msgCode: 1046},
    1047: {msg: 'Elementul trebuie să conțină o opțiune de galerie a momentelor.', msgCode: 1047},
    1048: {msg: 'Elementul trebuie să conțină o opțiune RSVP.', msgCode: 1048},
    1049: {msg: 'Elementul trebuie să conțină o opțiune de biografie a cuplului.', msgCode: 1049},
    1050: {msg: 'Elementul trebuie să conțină o opțiune WedSnap.', msgCode: 1050},
    1051: {msg: 'Elementul trebuie să conțină o opțiune de exportare RSVP.', msgCode: 1051},
    1052: {msg: 'Planul trebuie să conțină o opțiune de tablou de bord RSVP.', msgCode: 1052},
    1053: {msg: 'Elementul trebuie să conțină o opțiune de URL galerie suplimentară.', msgCode: 1053},
    1054: {msg: 'Elementul trebuie să conțină o opțiune de asistență suplimentară.', msgCode: 1054},
    1055: {msg: 'Elementul trebuie să conțină o opțiune de aplicație pentru momente.', msgCode: 1055},
    1056: {msg: 'Elementul trebuie să conțină o miniatură!', msgCode: 1056},
    1057: {msg: 'Elementul trebuie să conțină o descriere/rezumat!', msgCode: 1057},
    1058: {msg: 'Elementul trebuie să conțină o rată a galeriei sus!', msgCode: 1058},
    1059: {msg: 'Elementul trebuie să conțină o rată a imaginilor cuplului!', msgCode: 1059},
    1060: {msg: 'Elementul trebuie să conțină o rată a imaginilor cuplului!', msgCode: 1060},
    1061: {msg: 'Tema trebuie să conțină un status activ!', msgCode: 1061},
    1062: {msg: 'Elementul trebuie să conțină o imagine!', msgCode: 1062},
    1063: {msg: 'A intervenit o eroare la asocierea momentului cu nunta.', msgCode: 1063},
    1064: {msg: 'Momentul nu aparține acestei nunți.', msgCode: 1064},
    1065: {msg: 'Nunta nu există!', msgCode: 1065},
    1066: {msg: 'Fișierul nu a putut fi încărcat în acest moment. Te rugăm să încerci din nou.', msgCode: 1066},
    1072: {msg: 'Nu se poate salva elementul din cauza datelor solicitate lipsă', msgCode: 1072},
    1073: {msg: 'Numele/Prenumele este obligatoriu.', msgCode: 1073},
    1074: {msg: 'Mesajul este obligatoriu.', msgCode: 1074},
    1075: {msg: 'Numărul de telefon este obligatoriu.', msgCode: 1075},
    1076: {msg: 'Țara este obligatorie.', msgCode: 1076},
    1182: {msg: 'Verificarea codului Recaptcha a eșuat. Te rugăm să încerci din nou.', msgCode: 1182},
    1183: {msg: 'Codul Recaptcha este obligatoriu. Te rugăm să bifezi căsuța Nu sunt robot.', msgCode: 1183},
};

const getErrorMessage = (msgCode: number): string => {
    return messages[msgCode]["msg"] ?? "A aparut o eroare";
};

export {getErrorMessage};
